import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { supabase } from '../../lib/supabase'
import { Option } from '../../types/database'
import { formatOptionValue } from '../../utils/formatters'
import { usePopoverEditor } from '../../hooks/usePopoverEditor'

interface InlineOptionsEditorProps {
  aircraftId: number
  modelId: number
  currentOptions: string[]  // Current selected option IDs
  onSave: (newOptions: string[]) => Promise<void>
  onCancel: () => void
}

export const InlineOptionsEditor: React.FC<InlineOptionsEditorProps> = ({
  aircraftId: _aircraftId,
  modelId,
  currentOptions,
  onSave,
  onCancel
}) => {
  const [availableOptions, setAvailableOptions] = useState<Option[]>([])
  const [selectedOptions, setSelectedOptions] = useState<string[]>(currentOptions || [])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)

  // Use a ref to always have access to the latest selectedOptions in the click handler
  const selectedOptionsRef = useRef<string[]>(selectedOptions)
  useEffect(() => {
    selectedOptionsRef.current = selectedOptions
  }, [selectedOptions])

  // Fetch available options for this model
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const { data, error } = await supabase
          .from('options')
          .select('*')
          .eq('model_id', modelId)
          .order('name')

        if (error) throw error
        setAvailableOptions(data || [])
      } catch (err) {
        // Error fetching options
      } finally {
        setLoading(false)
      }
    }

    fetchOptions()
  }, [modelId])

  // Use a ref for saving state to avoid stale closures
  const savingRef = useRef(false)

  // The actual save function that uses refs to get latest values
  const doSave = useCallback(async () => {
    if (savingRef.current) return

    const latestSelected = selectedOptionsRef.current
    const sortedCurrent = [...(currentOptions || [])].sort()
    const sortedSelected = [...latestSelected].sort()
    const hasChanged = JSON.stringify(sortedCurrent) !== JSON.stringify(sortedSelected)

    if (!hasChanged) {
      onCancel()
      return
    }

    savingRef.current = true
    setSaving(true)
    try {
      await onSave(latestSelected)
      // Cache invalidation is now handled by MarketTable after save completes
    } catch (error) {
      savingRef.current = false
      setSaving(false)
    }
  }, [currentOptions, onSave, onCancel])

  const { position } = usePopoverEditor({
    onClickOutside: doSave,
    onCancel,
    popoverRef,
    anchorRef,
    popoverWidth: 280,
    popoverHeight: 320
  })

  const handleToggleOption = (optionId: string) => {
    setSelectedOptions(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId)
      } else {
        return [...prev, optionId]
      }
    })
  }

  const handleClearAll = () => {
    setSelectedOptions([])
  }

  const popoverContent = (
    <div
      ref={popoverRef}
      className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg p-2 min-w-[280px] max-h-[300px] overflow-y-auto"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' as const }}
      onClick={(e) => e.stopPropagation()}
    >
      {loading ? (
        <div className="text-sm text-gray-500 p-1">Loading options...</div>
      ) : availableOptions.length === 0 ? (
        <div className="text-sm text-gray-500 p-1">No options available for this model</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs font-medium text-gray-500">
              Select Options (click outside to save)
            </span>
            {selectedOptions.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-red-600 hover:text-red-800 hover:underline"
              >
                Clear All
              </button>
            )}
          </div>
          <div className="space-y-1">
            {availableOptions.map((option) => {
              const isSelected = selectedOptions.includes(option.option_id)
              return (
                <label
                  key={option.option_id}
                  className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                    isSelected ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleOption(option.option_id)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-900">{option.name}</span>
                  </div>
                  <span className="text-sm text-gray-500 ml-2">{formatOptionValue(option.value)}</span>
                </label>
              )
            })}
          </div>
          {saving && (
            <div className="text-xs text-gray-500 mt-2 px-1">Saving...</div>
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      <span ref={anchorRef} />
      {ReactDOM.createPortal(popoverContent, document.body)}
    </>
  )
}

export default InlineOptionsEditor

